/**
 * Central registry of backend module IDs — the single source of truth for the
 * `module_id` values used to scope documents/attachments and to drive the
 * CurrentModuleContextService producers.
 *
 * These values are taken from the backend's authoritative module list
 * (`GET /api/v1/document/modules`). Do NOT invent ids here — if a module isn't
 * in that response, it has no document module and must not be wired.
 */
export const MODULE_IDS = {
  PROPOSAL: 3,
  CHANGE_ORDER: 5,
  PURCHASE_ORDER: 6,
  TASK: 7,
  TICKET: 8,
  PRODUCT: 10,
  STAFF: 11,
  VENDOR: 12,
  CUSTOMER: 13,
  CONTACT: 15,
  BID: 16,
  WAREHOUSE: 17,
  SERVICE: 18,
  JOB: 19,
  SUBCONTRACTOR: 21,
  NOTE: 23,
  MILESTONE: 24,
  SUBMITTAL: 26,
  TASK_TEMPLATE: 28,
  JOB_SITE: 29,
  SCOPE: 31,
  TAKEOFF: 32,
  CALENDAR: 33,
  DOCUMENT: 34,
} as const;

/** Union of all known module-id values. */
export type ModuleId = (typeof MODULE_IDS)[keyof typeof MODULE_IDS];

/**
 * Overrides mapping a module id to the lowercase-plural REST resource path used
 * to fetch a single record (GET /api/v1/{path}/{id}).
 *
 * The module list returned by `GET /api/v1/modules/type` carries a `slug` that
 * for most modules already matches the REST route, but for some (e.g. Warehouse,
 * Subcontractor) it is the PascalCase display name — which is NOT a registered
 * route and 404s. Only modules whose slug differs from their route need an entry
 * here; every other module falls back to its slug (see resolveModuleResourcePath).
 */
export const MODULE_RESOURCE_PATHS: Partial<Record<ModuleId, string>> = {
  [MODULE_IDS.WAREHOUSE]: 'warehouses',
  [MODULE_IDS.SUBCONTRACTOR]: 'subcontractors',
};

/**
 * Resolve the REST resource path for a module record fetch, e.g. `warehouses`
 * for GET /api/v1/warehouses/{id}. Prefers the id-keyed override in
 * MODULE_RESOURCE_PATHS (stable, backend-independent) and falls back to the
 * backend-provided slug so modules that already work are unaffected.
 */
export function resolveModuleResourcePath(
  module: { id?: number | null; slug?: string | null } | null | undefined
): string {
  const override =
    module?.id != null
      ? MODULE_RESOURCE_PATHS[module.id as ModuleId]
      : undefined;
  return override ?? module?.slug ?? '';
}
